import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostBedsideSurveyRequest } from '../../../../shared/model/bedside-survey/PostBedsideSurveyRequest';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { MychartWebHeaderComponent } from "../../../../shared/components/mychart-web-header/mychart-web-header.component";
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GetFhirPatientSearchR4Response } from '../../../../shared/model/GetFhirPatientSearchR4Response';

@Component({
  selector: 'app-bedside-survey',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MychartWebHeaderComponent
],
  templateUrl: './bedside-survey.component.html',
  styleUrl: './bedside-survey.component.css'
})
export class BedsideSurveyComponent implements OnInit {
  surveyForm!: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;

  mcAppGateway = inject(MCAppGatewayService);
  auth : AuthService = inject(AuthService);

  locale: string = '';
  language: string = '';
  isMobileResolution: boolean = false;

  public eptID: string = '';
  public wprID: string = '';
  public fhirID: string = '';
  public patientFirstName: string | null = null;
  public patientLastName: string | null = null;
  public patientEmail: string | null = null;

  surveyQuestions = [
    { key: 'q1', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q1' },
    { key: 'q2', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q2' },
    { key: 'q3', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q3' },
    { key: 'q4', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q4' },
    { key: 'q5', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q5' },
    { key: 'q6', translationKey: 'BEDSIDE_SURVEY.QUESTIONS.Q6' }
  ];

  ratingOptions = [
    { value: 1, translationKey: 'BEDSIDE_SURVEY.RATING.STRONGLY_DISAGREE' },
    { value: 2, translationKey: 'BEDSIDE_SURVEY.RATING.DISAGREE' },
    { value: 3, translationKey: 'BEDSIDE_SURVEY.RATING.NEUTRAL' },
    { value: 4, translationKey: 'BEDSIDE_SURVEY.RATING.AGREE' },
    { value: 5, translationKey: 'BEDSIDE_SURVEY.RATING.STRONGLY_AGREE' }
  ];

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private mcAppGatewayService: MCAppGatewayService,
    private monitoringService: MonitoringService
  ) {
    // Set the default language for translations
    this.translate.setDefaultLang('en-US');
    const language = getLocaleFromUrl();
    this.translate.use(language);
  }

  ngOnInit(): void {
    this.initializeForm();

    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.wprID = sessionStorage.getItem("wprID") ?? "";
    this.fhirID = sessionStorage.getItem("fhirID") ?? "";
    this.patientFirstName = sessionStorage.getItem("patientFirstName") ?? "";
    this.patientLastName = sessionStorage.getItem("patientLastName") ?? "";
    this.patientEmail = sessionStorage.getItem("patientEmail") ?? "";
    
    // Auto-populate form if user is authenticated
    if (this.patientFirstName && this.patientLastName) {
      const fullName = `${this.patientFirstName} ${this.patientLastName}`;
      this.surveyForm.patchValue({
        patientName: fullName,
        patientEmail: this.patientEmail || ''
      });
    }
  }

  initializeForm(): void {
    const formConfig: any = {
      patientName: ['', Validators.required],
      patientEmail: ['', [Validators.required, Validators.email]],
      additionalComments: ['']
    };

    // Add form controls for each question (not required)
    this.surveyQuestions.forEach(question => {
      formConfig[question.key] = [''];
    });

    this.surveyForm = this.fb.group(formConfig);
  }

  onSubmit(): void {
    if (this.surveyForm.valid) {
      this.isSubmitting = true;
      this.submitSuccess = false;
      this.submitError = false;

      const request: PostBedsideSurveyRequest = {
        patientEptId: this.eptID,
        patientName: this.surveyForm.value.patientName,
        patientEmail: this.surveyForm.value.patientEmail,
        responses: {
          communicationWithStaff: this.surveyForm.value.q1 || null,
          communicationWithProviders: this.surveyForm.value.q2 || null,
          involvementInCare: this.surveyForm.value.q3 || null,
          careInformation: this.surveyForm.value.q4 || null,
          needsMet: this.surveyForm.value.q5 || null,
          medicationUnderstanding: this.surveyForm.value.q6 || null
        },
        additionalComments: this.surveyForm.value.additionalComments || ''
      };

      this.mcAppGatewayService.PostBedsideSurvey(request).subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          
          // Scroll to top to show message
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          if (response && response.success) {
            this.submitSuccess = true;
            this.submitError = false;
            this.monitoringService.logEvent('BedsideSurvey/Submit/Success', { 
              feature: 'bedside-survey', 
              platform: 'web',
              surveyId: response.surveyId 
            });
            
            // Reset form immediately after successful submission
            this.surveyForm.reset();
            
            // Hide success message after 3 seconds
            setTimeout(() => {
              this.submitSuccess = false;
            }, 5000);
          } else {
            // Response received but Success is false
            this.submitSuccess = false;
            this.submitError = true;
            this.monitoringService.logEvent('BedsideSurvey/Submit/Failed', { 
              feature: 'bedside-survey', 
              platform: 'web',
              message: response?.message || 'Unknown error'
            });
            console.error('Survey submission failed:', response?.message || 'Unknown error');
          }
        },
        error: (error: any) => {
          this.isSubmitting = false;
          this.submitSuccess = false;
          this.submitError = true;
          
          // Scroll to top to show error message
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          this.monitoringService.logEvent('BedsideSurvey/Submit/Error', { 
            feature: 'bedside-survey', 
            platform: 'web',
            error: error.message 
          });
          console.error('Error submitting bedside survey:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.surveyForm.controls).forEach(key => {
        this.surveyForm.get(key)?.markAsTouched();
      });
    }
  }

  selectRating(questionKey: string, value: number): void {
    this.surveyForm.get(questionKey)?.setValue(value);
    this.surveyForm.get(questionKey)?.markAsTouched();
  }

  isSelected(questionKey: string, value: number): boolean {
    return this.surveyForm.get(questionKey)?.value === value;
  }
}
